<script setup>
import SaveSubTaskAction from "@frontJs/components/actions/projectManagement/tasks/SaveSubTaskAction.vue";
import TaskActionsCell from "@frontJs/components/tables/cells/projectManagement/tasks/TaskActionsCell.vue";
import TaskDetailsCell from "@frontJs/components/tables/cells/projectManagement/tasks/TaskDetailsCell.vue";
import TaskOnHoldCell from "@frontJs/components/tables/cells/projectManagement/tasks/TaskOnHoldCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TaskPriorityCell from "@js/components/tables/cells/TaskPriorityCell.vue";
import TaskStatusCell from "@js/components/tables/cells/TaskStatusCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate, formatTime } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const {
  userSessionKey = null,
  type = null,
  status = null,
  task = null,
  theme = null,
  noOneResponsible = null,
  createTaskUrl,
  idea = false,
} = defineProps({
  userSessionKey: {
    type: String,
    required: false,
  },
  type: {
    type: [String, Array],
    required: false,
  },
  status: {
    type: [String, Array],
    required: false,
  },
  task: {
    type: Object,
    required: false,
  },
  theme: {
    type: [String, Array],
    required: false,
  },
  noOneResponsible: {
    type: Boolean,
    required: false,
  },
  createTaskUrl: {
    type: String,
    required: false,
  },
  idea: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "id",
      header: () => t("label.id"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (cellProps) =>
        h(TextCellV2, {
          text: cellProps.row.original.fullName,
          length: 160,
          lineThrough:
            (status === "open" ||
              status === "to_process" ||
              (status && status.includes("open")) ||
              (status && status.includes("to_process"))) &&
            cellProps.row.parentId &&
            (cellProps.row.original.status === "closed" ||
              cellProps.row.original.status === "processed"),
          minWidth: 400,
        }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (props) =>
        h(TaskDetailsCell, {
          row: props.row,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "taskManager",
      header: () => t("label.taskManager"),
      cell: (cellProps) =>
        h(TextCellV2, {
          text: cellProps.row.original.users
            .map((user) => user.fullName)
            .join(", "),
          lineThrough:
            (status === "open" ||
              status === "to_process" ||
              (status && status.includes("open")) ||
              (status && status.includes("to_process"))) &&
            cellProps.row.parentId &&
            (cellProps.row.original.status === "closed" ||
              cellProps.row.original.status === "processed"),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "status",
      header: () => t("label.status"),
      cell: (props) =>
        h(TaskStatusCell, {
          data: props.row.original,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "priority",
      header: () => t("label.priority"),
      cell: (props) =>
        h(TaskPriorityCell, {
          data: props.row.original,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "time",
      header: () => t("label.realTime"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatTime(
            props.row.original.hours,
            props.row.original.minutes,
          ),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "deadline",
      header: () => t("label.deadline"),
      cell: (cellProps) =>
        h(TextCellV2, {
          text: formatDate(cellProps.row.original.deadline),
          color:
            new Date(cellProps.row.original.deadline) < new Date()
              ? "red"
              : null,
          lineThrough:
            (status === "open" ||
              status === "to_process" ||
              (status && status.includes("open")) ||
              (status && status.includes("to_process"))) &&
            cellProps.row.parentId &&
            (cellProps.row.original.status === "closed" ||
              cellProps.row.original.status === "processed"),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "onHold",
      header: () => t("label.customerWaiting"),
      cell: (props) =>
        h(TaskOnHoldCell, {
          task: props.row.original,
          callback: updateData,
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      headerClasses: "justify-center",
      cell: (props) =>
        h(TaskActionsCell, {
          row: props.row,
          callback: updateData,
          idea,
        }),
    }),
  ],
  url: route("front.projectManagement.task.postTasks"),
  data: {
    type,
    status,
    taskId: task ? task.id : null,
    theme,
    noOneResponsible,
    idea,
  },
  userSessionKey,
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    >
      <template #actions>
        <SaveSubTaskAction
          v-if="task"
          :task="task"
          :callback="() => updateData()"
        >
          <template #button="{ setIsOpen }">
            <ActionButton
              size="small"
              :callback="() => setIsOpen(true)"
              :title="t('button.addSubTask')"
              :left-icon="PlusIcon"
            >
              {{ t("button.add") }}
            </ActionButton>
          </template>
        </SaveSubTaskAction>

        <ActionButton
          v-else
          size="small"
          :href="createTaskUrl"
          :left-icon="PlusIcon"
        >
          {{ t("button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
